import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ReactModal from 'react-modal';
import { modalStyle } from '../../../../../utils/modal';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { makeRequest } from '../../../../../utils/makeRequest';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import VirtualizedList from '../../../../../components/VirtualizedList';
import { Spinner } from '../../../../../components/Spinner';

function ConsumerMatchCandidatesModal({
  consumerId,
  refreshConsumer,
  finchMatchCandidates,
  setEmployeeView,
  closeModal,
}) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginAggregator, apiOriginConsumer } = useEnv();

  const [spinner, setSpinner] = useState(true);
  const [employees, setEmployees] = useState([]);

  const getFinchEmployees = useCallback(async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginAggregator}/finch/employees`,
      method: 'POST',
      data: JSON.stringify({ employees: finchMatchCandidates }),
    };

    try {
      const response = await makeRequest(config);

      setEmployees(response.employees);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  }, [apiOriginAggregator, getAccessTokenSilently, finchMatchCandidates]);

  useEffect(() => {
    if (finchMatchCandidates) {
      getFinchEmployees();
    } else {
      setSpinner(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finchMatchCandidates]);

  const linkEmployee = useCallback(
    async ({ connectionId, finchId, isActive, endDate }) => {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/link_employee`,
        method: 'POST',
        data: JSON.stringify({ consumerId, connectionId, finchId, isActive, endDate }),
      };

      try {
        await makeRequest(config);
        await refreshConsumer(consumerId);

        closeModal();
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    },
    [apiOriginConsumer, getAccessTokenSilently, consumerId, refreshConsumer, closeModal],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: 'firstName',
        header: 'First Name',
        width: 180,
      },
      {
        key: 'lastName',
        header: 'Last Name',
        width: 200,
      },
      {
        key: 'dob',
        header: 'Date of Birth',
        width: 180,
      },
      {
        key: 'ssn',
        header: 'Last 4 SSN Digits',
        width: 200,
      },
      {
        key: 'select',
        header: '',
        width: 80,
        renderValue: (_, row) => (
          <Button className="bo-common-inline-button" onClick={() => linkEmployee(row)}>
            Select
          </Button>
        ),
      },
      {
        key: 'view',
        header: '',
        width: 80,
        renderValue: (_, row) => (
          <Button className="bo-common-inline-button" onClick={() => setEmployeeView(row)}>
            View
          </Button>
        ),
      },
    ],
    [linkEmployee, setEmployeeView],
  );

  return (
    <ReactModal
      isOpen={!!finchMatchCandidates}
      style={modalStyle({
        width: '1080px',
        backgroundColor: 'rgb(248, 249, 250)',
        overlayBackgroundColor: 'transparent',
        overlayPosition: 'static',
      })}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
      onRequestClose={closeModal}
    >
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="mb-0">Matching Candidates</h5>
        <Button className="bo-common-base-button" onClick={closeModal}>
          <FontAwesomeIcon icon={faXmark} size="lg" color="#000000" />
        </Button>
      </div>
      <div className="bo-h-500 border">
        <Spinner visible={spinner}>
          <VirtualizedList
            tableRows={employees}
            tableColumns={tableColumns}
            width={1038}
            height={500}
            extendColumnKey="dob"
            rowKey="connectionId"
          />
        </Spinner>
      </div>
    </ReactModal>
  );
}

export default ConsumerMatchCandidatesModal;
